import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Zap, Shield, Loader2, TrendingUp, Gift, PartyPopper, Copy, PlayCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/lib/subscription";
import { useNavigate } from "react-router-dom";
import { acceptDemo } from "./AccessGate";
import ReferralLeaderboard from "./ReferralLeaderboard";
import PromoCountdown from "./PromoCountdown";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
const CURRENCY_META = {
  usd: { symbol: "$", flag: "🇺🇸", name: "USD" },
  eur: { symbol: "€", flag: "🇪🇺", name: "EUR" },
  gbp: { symbol: "£", flag: "🇬🇧", name: "GBP" },
  jpy: { symbol: "¥", flag: "🇯🇵", name: "JPY" },
  cny: { symbol: "¥", flag: "🇨🇳", name: "CNY" },
};
function formatPrice(amount, currency) {
  const meta = CURRENCY_META[currency] || CURRENCY_META.usd;
  if (currency === "jpy") return `${meta.symbol}${Math.round(amount).toLocaleString()}`;
  return `${meta.symbol}${amount.toFixed(2)}`;
}

function loadPayPalSdk() {
  return new Promise((resolve, reject) => {
    if (!PAYPAL_CLIENT_ID) return reject(new Error("PayPal not configured"));
    if (window.paypal) return resolve(window.paypal);
    const existing = document.querySelector("script[data-paypal-sdk]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription&currency=USD`;
    s.setAttribute("data-paypal-sdk", "true");
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function PayPalButton({ lookupKey, currency, onSuccess }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    if (!PAYPAL_CLIENT_ID) return;
    let cancelled = false;
    // PayPal SDK is loaded ONCE with a specific currency at page level.
    // We use the SDK with a fixed default currency (USD) then rely on the
    // subscription plan's currency during subscription.create. PayPal Buttons
    // will show localized amounts on the popup.
    loadPayPalSdk().then(paypal => {
      if (cancelled || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      paypal.Buttons({
        style: { layout: "horizontal", color: "gold", shape: "pill", label: "subscribe", height: 44 },
        createSubscription: async (data, actions) => {
          const res = await axios.post(`${API}/billing/paypal/create-subscription`, {
            lookup_key: lookupKey, currency,
          });
          return actions.subscription.create({ plan_id: res.data.plan_id });
        },
        onApprove: async (data) => {
          try {
            await axios.post(`${API}/billing/paypal/activate`, {
              subscription_id: data.subscriptionID,
              lookup_key: lookupKey,
              currency,
            });
            toast.success("PayPal subscription activated");
            onSuccess?.();
          } catch {
            toast.error("Failed to activate subscription");
          }
        },
        onError: (err) => {
          console.error(err);
          setError("PayPal error");
        },
      }).render(containerRef.current).then(() => setReady(true)).catch(() => setError("Failed to render"));
    }).catch(() => setError("PayPal unavailable"));
    return () => { cancelled = true; };
  }, [lookupKey, currency, onSuccess]);

  if (!PAYPAL_CLIENT_ID) {
    return <div className="text-xs text-zinc-500 text-center py-2">PayPal — {t("paypal_coming_soon") || "coming soon"}</div>;
  }
  return (
    <div>
      <div ref={containerRef} data-testid={`paypal-button-${lookupKey}-${currency}`} className="min-h-[44px]" />
      {error && <div className="text-xs text-red-400 mt-1">{error}</div>}
      {!ready && !error && <div className="text-xs text-zinc-500 flex items-center gap-2 justify-center py-1"><Loader2 className="w-3 h-3 animate-spin" />{t("paypal_loading") || "Loading PayPal…"}</div>}
    </div>
  );
}

export default function PricingPage() {
  const { t, dir } = useI18n();
  const { user, login } = useAuth();
  const { trial_active, trial_days_left, refresh, has_access } = useSubscription();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [promo, setPromo] = useState(null);
  const [currencies, setCurrencies] = useState(["usd", "eur", "gbp", "jpy", "cny"]);
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem("forex.currency") || "usd"; } catch { return "usd"; }
  });
  const [busy, setBusy] = useState(null);
  const [refCode, setRefCode] = useState(null);
  const [promoCopied, setPromoCopied] = useState(false);
  const [rates, setRates] = useState(null);

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then(r => {
      setPlans(r.data.plans || []);
      setPromo(r.data.promo || null);
      if (r.data.currencies) setCurrencies(r.data.currencies);
    });
    axios.get(`${API}/billing/rates?base=usd`).then(r => setRates(r.data)).catch(() => setRates(null));
    try {
      const r = localStorage.getItem("forex.ref");
      if (r) setRefCode(r);
    } catch { /* ignore */ }
  }, []);

  // Convert an amount in the selected currency back to USD for tooltip display.
  function toUsd(amount, curr) {
    if (!rates || !amount) return null;
    const rate = rates.rates?.[curr.toUpperCase()];
    if (!rate) return null;
    return amount / rate; // rates are {base=USD, EUR: 0.93, ...} so 1 USD = 0.93 EUR
  }

  function copyPromo() {
    if (!promo?.code) return;
    navigator.clipboard.writeText(promo.code);
    setPromoCopied(true);
    setTimeout(() => setPromoCopied(false), 1800);
  }

  function pickCurrency(c) {
    setCurrency(c);
    try { localStorage.setItem("forex.currency", c); } catch { /* ignore */ }
  }

  async function startStripe(lookupKey) {
    if (!user) { login(); return; }
    setBusy(lookupKey);
    try {
      const { data } = await axios.post(`${API}/billing/stripe/checkout`, {
        lookup_key: lookupKey,
        currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  }

  function tryFreeDemo() {
    acceptDemo();
    navigate("/", { replace: true });
  }

  const features = [
    t("feat_live") || "Real-time signals for 21 forex & crypto pairs",
    t("feat_ai") || "AI-powered technical analysis (Claude Sonnet 4.5)",
    t("feat_timeframes") || "All timeframes: 1m to 1d",
    t("feat_best_hours") || "Personal best hours & win-rate stats",
    t("feat_alerts") || "Telegram + sound alerts",
    t("feat_market_clock") || "Global market session clock",
  ];

  return (
    <div dir={dir} className="min-h-screen text-zinc-100 flex flex-col items-center justify-center px-4 py-12 relative">
      {/* Language switcher (fixed corner, opposite of dir) */}
      <div className={"absolute top-4 z-20 " + (dir === "rtl" ? "left-4" : "right-4")}>
        <LanguageSwitcher />
      </div>
      <div className="max-w-5xl w-full">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300 mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {trial_active
              ? (t("trial_ends_in") || "Free trial ends in") + ` ${trial_days_left} ` + (t("days") || "days")
              : (t("start_trial") || "Start your 7-day free trial")}
          </div>
          {refCode && !user && (
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 px-3 py-1 text-xs mb-4 ml-2">
              <Gift className="w-3.5 h-3.5" />
              <span>
                {t("referral_bonus_banner") || "Referral bonus: sign in to get +30 extra trial days"} · <span className="font-mono font-bold">{refCode}</span>
              </span>
            </div>
          )}
          {promo && (
            <div data-testid="launch-promo-banner" className="mt-4 mx-auto max-w-2xl rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 via-emerald-500/10 to-amber-500/15 px-4 py-3 flex items-center gap-3 flex-wrap">
              <PartyPopper className="w-5 h-5 text-amber-300 shrink-0" />
              <div className="text-start flex-1 min-w-0">
                <div className="text-sm text-amber-200 font-semibold">
                  {t("launch_promo_title") || "Launch promo"}
                  {promo.kind === "percent"
                    ? ` · ${promo.value}% ${t("promo_off") || "off"}`
                    : ` · $${promo.value} ${t("promo_off") || "off"}`}
                </div>
                <div className="text-[11px] text-zinc-400">
                  {t("promo_hint") || "Enter this code on the Stripe checkout page:"}
                </div>
              </div>
              <button
                data-testid="copy-launch-promo"
                onClick={copyPromo}
                dir="ltr"
                className="font-mono font-black tracking-widest text-amber-200 bg-black/40 rounded-md px-3 py-1.5 border border-amber-400/40 hover:bg-black/60 flex items-center gap-2"
              >
                {promo.code}
                <Copy className="w-3.5 h-3.5 opacity-70" />
              </button>
              {promo.expires_at && (
                <PromoCountdown expiresAt={promo.expires_at} />
              )}
              {promoCopied && <span className="text-emerald-400 text-xs">{t("copied") || "Copied!"}</span>}
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-zinc-400">
            {t("pricing_title") || "Trade smarter with pro signals"}
          </h1>
          <p className="mt-4 text-zinc-400 text-base max-w-2xl mx-auto">
            {t("pricing_sub") || "One-month free trial. No credit-card commitment. Cancel anytime."}
          </p>

          {/* Currency picker */}
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap" data-testid="currency-picker">
            <span className="text-xs text-zinc-500">{t("currency") || "Currency"}:</span>
            {currencies.map(c => {
              const meta = CURRENCY_META[c] || CURRENCY_META.usd;
              const active = c === currency;
              return (
                <button
                  key={c}
                  data-testid={`currency-${c}`}
                  onClick={() => pickCurrency(c)}
                  className={
                    "px-3 h-8 rounded-full text-xs font-semibold border transition " +
                    (active
                      ? "bg-emerald-500 text-black border-emerald-400"
                      : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700")
                  }
                >
                  <span className="mr-1">{meta.flag}</span>{meta.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(dir === "rtl" ? [...plans].reverse() : plans).map(p => {
            const isYearly = p.lookup_key === "signals_yearly";
            const highlighted = p.highlight;
            const priceAmount = (p.prices && p.prices[currency]) ?? 0;
            return (
              <div
                key={p.lookup_key}
                data-testid={`plan-card-${p.lookup_key}`}
                className={
                  "relative rounded-2xl border p-6 sm:p-8 bg-gradient-to-b text-start " +
                  (highlighted
                    ? "border-emerald-500/40 from-emerald-500/10 to-transparent shadow-2xl shadow-emerald-900/40"
                    : "border-zinc-800 from-zinc-900/70 to-zinc-950/60")
                }
              >
                {highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 text-black text-[10px] font-bold px-3 py-1 tracking-wider uppercase whitespace-nowrap">
                    {t("best_value") || "Best Value"}
                  </div>
                )}
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                  {isYearly ? <TrendingUp className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                  <span>{isYearly ? (t("plan_yearly") || "Yearly") : (t("plan_monthly") || "Monthly")}</span>
                </div>
                <div className="flex items-baseline gap-2" dir="ltr">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span data-testid={`plan-price-${p.lookup_key}`} className="text-5xl font-black text-white cursor-help">{formatPrice(priceAmount, currency)}</span>
                      </TooltipTrigger>
                      {currency !== "usd" && toUsd(priceAmount, currency) != null && (
                        <TooltipContent side="top" className="bg-zinc-900 border-zinc-800 text-zinc-200 text-xs">
                          ≈ ${toUsd(priceAmount, currency).toFixed(2)} USD
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  <span className="text-zinc-500 text-sm">/ {isYearly ? (t("year") || "year") : (t("month") || "month")}</span>
                </div>
                {isYearly && (
                  <div className="text-emerald-400 text-xs mt-1 flex items-center gap-1 flex-wrap">
                    <span dir="ltr">≈ {formatPrice(priceAmount / 12, currency)}/{t("month") || "month"}</span>
                    <span>— {t("save_1") || "save vs monthly"}</span>
                  </div>
                )}
                <ul className="mt-6 space-y-2.5 text-sm text-zinc-300">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 space-y-3">
                  <Button
                    data-testid={`subscribe-stripe-${p.lookup_key}`}
                    onClick={() => startStripe(p.lookup_key)}
                    disabled={busy === p.lookup_key}
                    className={
                      "w-full h-11 rounded-full font-semibold " +
                      (highlighted
                        ? "bg-emerald-500 hover:bg-emerald-400 text-black"
                        : "bg-white hover:bg-zinc-200 text-black")
                    }
                  >
                    {busy === p.lookup_key ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("redirecting") || "Redirecting…"}</>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <span>{t("pay_card") || "Pay with card"}</span>
                        <span className="opacity-70">·</span>
                        <span dir="ltr">Stripe</span>
                      </span>
                    )}
                  </Button>
                  <div className="text-[11px] text-zinc-500 text-center">— {t("or") || "or"} —</div>
                  <PayPalButton lookupKey={p.lookup_key} currency={currency} onSuccess={refresh} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Try free demo CTA (only if user has no active access) */}
        {user && !has_access && (
          <div className="mt-8 flex flex-col items-center gap-2">
            <button
              data-testid="try-demo-btn"
              onClick={tryFreeDemo}
              className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 text-sm underline underline-offset-4 decoration-dotted"
            >
              <PlayCircle className="w-4 h-4" />
              {t("try_free_demo") || "Or skip and try 2 pairs free (EUR/USD + BTC/USDT)"}
            </button>
            <div className="text-[10px] text-zinc-500">{t("try_free_demo_hint") || "No sign-up commitment — you can upgrade anytime."}</div>
          </div>
        )}

        <ReferralLeaderboard />

        <div className="mt-8 flex items-center justify-center gap-6 text-xs text-zinc-500">
          <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> {t("secure_stripe") || "Secured by Stripe & PayPal"}</div>
          <div>· {t("cancel_anytime") || "Cancel anytime"}</div>
        </div>
      </div>
    </div>
  );
}
